import { ArrowForward } from "@mui/icons-material";
import { Box, ButtonBase, Divider, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { useId } from "react";

import { PRODUCT_ICONS } from "components/common/navIcons";
import {
  CALIBRATE_FONT_FAMILY_ACCENT,
  CALIBRATE_SURFACE_HAIRLINE,
  NAV_SECTION_COLORS,
  NavSectionColors,
} from "components/hoc/Theme";
import { VIEWPORT_2K_MIN_MEDIA, VIEWPORT_4K_MIN_MEDIA } from "config";
import type { NavCatalog, NavProduct, NavSection } from "utils/navModel";

/**
 * The home page's products in the sidebar's sections: a tile per product, and
 * the small tiles of the Management row after a divider. Every tile is one
 * link, so it opens in a new tab like any link.
 */

// Five tiles fit a row at 1440 px wide, sidebar included.
const TILE_W = 216;
const TILE_W_2K = 280;
const TILE_W_4K = 313;

const sectionTitleSx = (color: string) => ({
  m: 0,
  mb: 1.25,
  fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.3,
  letterSpacing: "0.02em",
  textTransform: "uppercase" as const,
  color,
  [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 19, mb: 1.5 },
  [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 20 },
});

const iconBoxSx = (colors: NavSectionColors, size: number) => ({
  width: size,
  height: size,
  borderRadius: 1,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: colors.iconBackground,
  color: colors.icon,
});

const tileSurfaceSx = (theme: Theme) => ({
  bgcolor: "background.paper",
  borderRadius: 1,
  border: `1px solid ${CALIBRATE_SURFACE_HAIRLINE}`,
  boxShadow: theme.shadows[2],
  transition: "box-shadow 0.2s ease",
  textDecoration: "none",
  color: "text.primary",
  "&:hover": { boxShadow: theme.shadows[8] },
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
});

function ProductTile({
  product,
  colors,
}: {
  product: NavProduct;
  colors: NavSectionColors;
}) {
  const theme = useTheme();
  const titleId = useId();
  const descriptionId = useId();
  if (product.tile?.kind !== "product") return null;
  const { description, cta } = product.tile;
  return (
    <ButtonBase
      href={product.path}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      sx={{
        ...tileSurfaceSx(theme),
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        textAlign: "left",
        width: "100%",
        height: "100%",
        minHeight: { xs: 0, sm: 188 },
        boxSizing: "border-box",
        p: 1.75,
        gap: 1,
        [VIEWPORT_2K_MIN_MEDIA]: { minHeight: 220, p: 2.5 },
        [VIEWPORT_4K_MIN_MEDIA]: { minHeight: 236 },
      }}
    >
      <Box
        sx={{
          ...iconBoxSx(colors, 40),
          [VIEWPORT_2K_MIN_MEDIA]: { width: 52, height: 52 },
          [VIEWPORT_4K_MIN_MEDIA]: { width: 56, height: 56 },
        }}
      >
        {PRODUCT_ICONS[product.key]({
          fontSize: 24,
          color: "inherit",
          [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 30 },
          [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 32 },
        })}
      </Box>
      <Typography
        component="h3"
        id={titleId}
        sx={{
          m: 0,
          mt: 0.5,
          ...theme.typography.calibrateDisplay,
          fontSize: 16,
          lineHeight: 1.25,
          color: "text.primary",
          [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 19 },
          [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 20 },
        }}
      >
        {product.label}
      </Typography>
      <Typography
        id={descriptionId}
        sx={{
          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
          fontSize: 13,
          lineHeight: 1.5,
          color: "text.secondary",
          [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 15 },
          [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 16 },
        }}
      >
        {description}
      </Typography>
      <Box sx={{ flex: "1 1 auto" }} />
      {/* Inline, so a call to action that wraps keeps its arrow on the
          last word. */}
      <Box
        component="span"
        sx={{
          display: "block",
          pt: 0.5,
          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.3,
          color: colors.cta ?? colors.header,
          [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 16 },
          [VIEWPORT_4K_MIN_MEDIA]: { fontSize: 17 },
        }}
      >
        {cta}
        {"\u00a0"}
        <ArrowForward
          aria-hidden
          sx={{ fontSize: "1.1em", verticalAlign: "-0.2em" }}
        />
      </Box>
    </ButtonBase>
  );
}

function SmallTile({
  product,
  colors,
}: {
  product: NavProduct;
  colors: NavSectionColors;
}) {
  const theme = useTheme();
  return (
    <ButtonBase
      href={product.path}
      sx={{
        ...tileSurfaceSx(theme),
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 1.25,
        width: { xs: "100%", sm: TILE_W },
        minHeight: 52,
        px: 1.5,
        py: 1,
        boxSizing: "border-box",
        textAlign: "left",
        [VIEWPORT_2K_MIN_MEDIA]: { width: TILE_W_2K, minHeight: 60 },
        [VIEWPORT_4K_MIN_MEDIA]: { width: TILE_W_4K },
      }}
    >
      <Box sx={iconBoxSx(colors, 30)}>
        {PRODUCT_ICONS[product.key]({ fontSize: 18, color: "inherit" })}
      </Box>
      <Typography
        component="h3"
        sx={{
          m: 0,
          fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
          fontSize: 15,
          fontWeight: 500,
          lineHeight: 1.3,
          color: "text.primary",
          [VIEWPORT_2K_MIN_MEDIA]: { fontSize: 17 },
        }}
      >
        {product.label}
      </Typography>
    </ButtonBase>
  );
}

/** The Management row: small tiles, no descriptions or calls to action. */
const isSmallSection = (section: NavSection) =>
  section.products.every((product) => product.tile?.kind === "small");

function Section({ section }: { section: NavSection }) {
  const headingId = useId();
  const colors = NAV_SECTION_COLORS[section.key];
  const small = isSmallSection(section);
  return (
    <Box component="section" aria-labelledby={headingId}>
      <Typography
        component="h2"
        id={headingId}
        sx={sectionTitleSx(colors.header)}
      >
        {section.title}
      </Typography>
      {small ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.75 }}>
          {section.products.map((product) => (
            <SmallTile key={product.key} product={product} colors={colors} />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            // As many tiles as fit, left-aligned; a row takes its tallest tile.
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: `repeat(auto-fill, ${TILE_W}px)`,
            },
            justifyContent: "start",
            gap: 1.75,
            [VIEWPORT_2K_MIN_MEDIA]: {
              gridTemplateColumns: `repeat(auto-fill, ${TILE_W_2K}px)`,
              gap: 3,
            },
            [VIEWPORT_4K_MIN_MEDIA]: {
              gridTemplateColumns: `repeat(auto-fill, ${TILE_W_4K}px)`,
            },
          }}
        >
          {section.products.map((product) => (
            <ProductTile key={product.key} product={product} colors={colors} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function HomeSections({ catalog }: { catalog: NavCatalog }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3.5,
        [VIEWPORT_2K_MIN_MEDIA]: { gap: 4.5 },
      }}
    >
      {catalog.sections.map((section, index) => (
        <Box
          key={section.key}
          sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}
        >
          {/* The Management row sits apart from the product sections. */}
          {isSmallSection(section) && index > 0 && <Divider />}
          <Section section={section} />
        </Box>
      ))}
    </Box>
  );
}
